import { useEffect, useState } from 'react';
import { simulateCurrentLighting } from '@neopolis/engine';
import type { Content, GameState } from '@neopolis/engine';

import { T } from '../../textes-interface.js';
import { RueDeNuit } from './RueDeNuit.js';

/** L'éclairage d'aujourd'hui, tel que le moteur le décrit : allumé toute la nuit. */
const NUIT_ACTUELLE = simulateCurrentLighting().moments;

const DUREE_PAR_HEURE = 700;

/**
 * Étape 1 — Comprendre.
 *
 * L'animation montre le lampadaire d'aujourd'hui sur un cycle complet : allumé
 * sans interruption, y compris à trois heures du matin dans une rue déserte.
 * Les trois questions guidées viennent ensuite, réponses comprises, depuis le
 * contenu.
 */
export function EtapeComprendre({
  content,
  partie,
  onRepondre,
}: {
  content: Content;
  partie: GameState;
  onRepondre: (questionId: string, choiceId: string) => void;
}) {
  const [index, setIndex] = useState(0);
  const [enCours, setEnCours] = useState(true);

  useEffect(() => {
    if (!enCours) {
      return undefined;
    }
    const minuteur = setTimeout(() => {
      setIndex((precedent) => {
        if (precedent + 1 >= NUIT_ACTUELLE.length) {
          setEnCours(false);
          return precedent;
        }
        return precedent + 1;
      });
    }, DUREE_PAR_HEURE);
    return () => clearTimeout(minuteur);
  }, [enCours, index]);

  const chapitre = content.chapitres.find((c) => c.id === partie.chapterId);
  const questions = chapitre?.questions_guidees ?? [];
  const moment = NUIT_ACTUELLE[index] ?? NUIT_ACTUELLE[0]!;

  return (
    <div className="space-y-5">
      <section className="rounded-2xl border-2 border-slate-200 bg-white p-4">
        <h2 className="text-lg font-bold text-slate-900">{T.comprendre.titreAnimation}</h2>
        <div className="mt-3">
          <RueDeNuit moment={moment} />
        </div>
        <p className="mt-2 text-slate-700">{T.comprendre.legendeAnimation}</p>

        <button
          type="button"
          onClick={() => {
            setIndex(0);
            setEnCours(true);
          }}
          className="mt-3 min-h-touch rounded-xl bg-slate-100 px-4 font-semibold text-slate-700 hover:bg-slate-200"
        >
          {T.comprendre.rejouer}
        </button>
      </section>

      <section className="space-y-3">
        <h2 className="text-lg font-bold text-slate-900">{T.comprendre.questions}</h2>

        {questions.map((question) => {
          const reponse = partie.guidedAnswers[question.id];
          const choisi = question.choix.find((choix) => choix.id === reponse);

          return (
            <fieldset
              key={question.id}
              className="rounded-2xl border-2 border-slate-200 bg-white p-4"
            >
              <legend className="px-1 font-semibold text-slate-900">{question.question}</legend>

              <div className="mt-2 space-y-2">
                {question.choix.map((choix) => {
                  const selectionne = reponse === choix.id;
                  const couleur = !selectionne
                    ? 'border-slate-300 bg-white text-slate-800 hover:bg-slate-50'
                    : choix.correct
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-900'
                      : 'border-rose-400 bg-rose-50 text-rose-900';

                  return (
                    <button
                      key={choix.id}
                      type="button"
                      aria-pressed={selectionne}
                      onClick={() => onRepondre(question.id, choix.id)}
                      className={`min-h-touch w-full rounded-xl border-2 px-4 py-2 text-left font-medium ${couleur}`}
                    >
                      {choix.texte}
                      {selectionne && (
                        <span className="ml-2 text-sm font-bold">
                          {choix.correct ? T.comprendre.bonneReponse : T.comprendre.mauvaiseReponse}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {choisi && (
                <p className="mt-3 rounded-xl bg-slate-50 p-3 text-slate-800">
                  {question.explication}
                </p>
              )}
            </fieldset>
          );
        })}
      </section>
    </div>
  );
}
